import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye, EyeOff } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { CuisineComboboxMulti } from "@/components/cuisine-combobox";
import { MAX_SPECIALTIES } from "@/lib/chef-taxonomy";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/use-auth";
import { useLaunchPromo } from "@/lib/use-launch-promo";
import { friendlyError } from "@/lib/friendly-error";
import { useAutosave, useUnsavedGuard } from "@/lib/use-autosave";
import { SaveStatusIndicator } from "@/components/save-status";
import { requireVerifiedChef } from "@/lib/require-verified-chef";

export const Route = createFileRoute("/_authenticated/dashboard/profilo")({
  head: () => ({ meta: [{ title: "Profilo Chef — ChefConnect" }] }),
  beforeLoad: requireVerifiedChef,
  component: ChefProfileEdit,
});

function slugify(s: string) {
  return s.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim().replace(/\s+/g, "-").slice(0, 60);
}

const profileSchema = z.object({
  display_name: z.string().trim()
    .min(3, "Il nome deve contenere almeno 3 caratteri. Esempio: Mario Rossi")
    .max(80, "Il nome può contenere al massimo 80 caratteri."),
  headline: z.string().trim().max(120, "Lo slogan può contenere al massimo 120 caratteri.").optional().or(z.literal("")),
  bio: z.string().trim()
    .max(2000, "La biografia può contenere al massimo 2000 caratteri.")
    .refine((v) => v === "" || v.length >= 80, {
      message: "La biografia deve contenere almeno 80 caratteri. Esempio: «Chef specializzato nella cucina mediterranea con oltre 12 anni di esperienza nei migliori ristoranti d'Italia.»",
    })
    .optional().or(z.literal("")),
  city: z.string().trim().min(2, "Inserisci una città valida. Esempio: Milano").max(80),
  region: z.string().trim().max(60).optional().or(z.literal("")),
  hourly_rate: z.string().refine((v) => v === "" || (!Number.isNaN(Number(v)) && Number(v) >= 0 && Number(v) <= 1000), {
    message: "Inserisci un importo valido tra 0 e 1000 €. Esempio: 80",
  }),
  years_experience: z.string().refine((v) => v === "" || (Number.isInteger(Number(v)) && Number(v) >= 0 && Number(v) <= 80), {
    message: "Inserisci un numero intero tra 0 e 80.",
  }),
  whatsapp: z.string().trim().refine((v) => v === "" || /^\+?[0-9 ]{6,20}$/.test(v), {
    message: "Inserisci un numero valido con prefisso internazionale. Esempio: +39 333 1234567",
  }),
  cuisines: z.array(z.string()),
  specialties: z.array(z.string()).max(MAX_SPECIALTIES, `Massimo ${MAX_SPECIALTIES} specialità.`),
  languages: z.string(),
});
type FieldErrors = Partial<Record<keyof z.infer<typeof profileSchema>, string>>;

function ChefProfileEdit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: promo } = useLaunchPromo();

  const chefQ = useQuery({
    enabled: !!user,
    queryKey: ["my-chef", user?.id],
    queryFn: async () => {
      const { data } = await supabase.from("chefs").select("*").eq("user_id", user!.id).maybeSingle();
      return data;
    },
  });

  // WhatsApp vive in tabella separata `chef_contacts` (P0-1): non più esposto via Data API pubblica.
  const contactQ = useQuery({
    enabled: !!user,
    queryKey: ["my-chef-contact", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("chef_contacts")
        .select("whatsapp, whatsapp_public")
        .eq("chef_id", user!.id)
        .maybeSingle();
      return data;
    },
  });

  const [form, setForm] = useState({
    display_name: "",
    headline: "",
    bio: "",
    city: "",
    region: "",
    hourly_rate: "",
    years_experience: "",
    cuisines: [] as string[],
    specialties: [] as string[],
    languages: "Italiano",
    whatsapp: "",
    whatsapp_public: false,
    is_visible: true,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  // Sprint 21.6 — autosave: attivo solo se il profilo esiste già.
  // Per un profilo nuovo lasciamo il flusso manuale (serve il consenso esplicito
  // dell'utente per attivare bonus Founder).
  const autosaveEnabled = !!chefQ.data && !!user;

  useEffect(() => {
    if (chefQ.data) {
      setForm({
        display_name: chefQ.data.display_name ?? "",
        headline: chefQ.data.headline ?? "",
        bio: chefQ.data.bio ?? "",
        city: chefQ.data.city ?? "",
        region: chefQ.data.region ?? "",
        hourly_rate: chefQ.data.hourly_rate?.toString() ?? "",
        years_experience: chefQ.data.years_experience?.toString() ?? "",
        cuisines: chefQ.data.cuisines ?? [],
        specialties: chefQ.data.specialties ?? [],
        languages: (chefQ.data.languages ?? []).join(", ") || "Italiano",
        whatsapp: contactQ.data?.whatsapp ?? "",
        whatsapp_public: !!contactQ.data?.whatsapp_public,
        is_visible: chefQ.data.is_visible !== false,
      });
    }
  }, [chefQ.data, contactQ.data]);

  const isNew = !chefQ.data;

  async function persist(value: typeof form) {
    if (!user) throw new Error("Sessione non valida.");
    const payload = {
      user_id: user.id,
      slug: chefQ.data?.slug ?? `${slugify(value.display_name)}-${user.id.slice(0, 6)}`,
      display_name: value.display_name.trim(),
      headline: value.headline.trim() || null,
      bio: value.bio.trim() || null,
      city: value.city.trim() || null,
      region: value.region.trim() || null,
      hourly_rate: value.hourly_rate ? Number(value.hourly_rate) : null,
      years_experience: value.years_experience ? Number(value.years_experience) : null,
      cuisines: value.cuisines,
      specialties: value.specialties.slice(0, MAX_SPECIALTIES),
      languages: value.languages.split(",").map((s) => s.trim()).filter(Boolean),
      is_visible: value.is_visible,
    };
    const { error } = await supabase.from("chefs").upsert(payload, { onConflict: "user_id" });
    if (error) throw error;
    const wa = value.whatsapp.trim();
    const { error: contactErr } = await supabase
      .from("chef_contacts")
      .upsert(
        { chef_id: user.id, whatsapp: wa || null, whatsapp_public: !!wa && value.whatsapp_public },
        { onConflict: "chef_id" },
      );
    if (contactErr) throw contactErr;
  }

  const autosave = useAutosave({
    value: form,
    enabled: autosaveEnabled,
    delay: 2500,
    canSave: (v) => profileSchema.safeParse(v).success,
    save: persist,
    toMessage: (e) => friendlyError(e, "Non è stato possibile salvare. Riproveremo automaticamente."),
  });

  useUnsavedGuard(autosave.isDirty);

  // Resetta lo stato "dirty" quando arriva una nuova baseline dal server.
  useEffect(() => {
    if (chefQ.data) autosave.markPristine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chefQ.data?.user_id]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setErrors({});
    const parsed = profileSchema.safeParse(form);
    if (!parsed.success) {
      const fe: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FieldErrors;
        if (key && !fe[key]) fe[key] = issue.message;
      }
      setErrors(fe);
      toast.error("Alcuni campi non sono validi. Controlla gli errori evidenziati.");
      return;
    }
    setSaving(true);
    try {
      await persist(form);
      if (isNew && promo?.active && promo.remaining > 0) {
        toast.success("🎁 Profilo creato! Hai ricevuto 30 giorni di Premium gratis + badge Founder.", { duration: 6000 });
      } else {
        toast.success("Profilo aggiornato. Le modifiche sono già online.");
      }
      navigate({ to: "/dashboard" } as never);
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error("[profile-save]", err);
      toast.error(friendlyError(err, "Non è stato possibile salvare il profilo. Riprova tra qualche istante."));
    } finally {
      setSaving(false);
    }
  };

  const fieldErr = (k: keyof FieldErrors) =>
    errors[k] ? <p className="text-xs text-destructive">{errors[k]}</p> : null;

  return (
    <div className="container mx-auto max-w-3xl px-4 py-10">
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Torna alla dashboard
      </Link>
      <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <h1 className="font-display text-4xl">{isNew ? "Crea il tuo profilo Chef" : "Modifica profilo"}</h1>
        {chefQ.data?.slug && (
          <Button asChild variant="outline" size="sm">
            <Link to="/chef/$slug" params={{ slug: chefQ.data.slug }} target="_blank" rel="noopener noreferrer">
              <Eye className="mr-1.5 h-4 w-4" /> Anteprima del mio profilo
            </Link>
          </Button>
        )}
      </div>
      {!isNew && (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span>Vedi subito come appari ai clienti.</span>
          <SaveStatusIndicator status={autosave.status} error={autosave.error} />
        </div>
      )}
      {isNew && promo?.active && promo.remaining > 0 && (
        <div className="mt-4 rounded-md border border-gold/40 bg-gold-muted/30 p-4 text-sm">
          🎁 <strong>Offerta di lancio attiva:</strong> appena salvi il profilo riceverai automaticamente 30 giorni di Premium gratis e il badge Founder permanente. <strong>{promo.remaining}/{promo.max_chefs}</strong> posti ancora disponibili.
        </div>
      )}

      <form onSubmit={onSave} className="mt-8 space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="display_name">Nome pubblico*</Label>
            <Input id="display_name" required aria-invalid={!!errors.display_name} value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="es. Chef Marco Rossi" />
            {fieldErr("display_name") ?? <p className="text-xs text-muted-foreground">Il nome con cui apparirai pubblicamente (min. 3 caratteri).</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="headline">Slogan / titolo</Label>
            <Input id="headline" aria-invalid={!!errors.headline} value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="es. Cucina italiana contemporanea a domicilio" />
            {fieldErr("headline")}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="bio">Biografia</Label>
          <Textarea id="bio" rows={5} aria-invalid={!!errors.bio} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Racconta la tua storia, formazione, esperienze..." />
          {fieldErr("bio") ?? <p className="text-xs text-muted-foreground">Consigliato: almeno 80 caratteri. {form.bio.length}/2000.</p>}
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="city">Città*</Label>
            <Input id="city" required aria-invalid={!!errors.city} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="es. Milano" />
            {fieldErr("city")}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="region">Regione</Label>
            <Input id="region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="es. Lombardia" />
            {fieldErr("region")}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="years">Anni di esperienza</Label>
            <Input id="years" type="number" min={0} max={80} aria-invalid={!!errors.years_experience} value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} placeholder="es. 12" />
            {fieldErr("years_experience")}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="rate">Tariffa oraria (€)</Label>
            <Input id="rate" type="number" min={0} max={1000} aria-invalid={!!errors.hourly_rate} value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} placeholder="es. 80" />
            {fieldErr("hourly_rate") ?? <p className="text-xs text-muted-foreground">Importo in euro all'ora (lascia vuoto se preferisci concordare).</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wa">WhatsApp (con prefisso)</Label>
            <Input id="wa" aria-invalid={!!errors.whatsapp} value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="es. +39 333 1234567" />
            {fieldErr("whatsapp") ?? <p className="text-xs text-muted-foreground">Includi il prefisso internazionale (es. +39 per l'Italia).</p>}
            <label className="mt-2 flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                checked={form.whatsapp_public}
                onChange={(e) => setForm({ ...form, whatsapp_public: e.target.checked })}
                className="mt-0.5 h-3.5 w-3.5 accent-gold"
              />
              <span className="text-muted-foreground">
                Mostra un pulsante <strong className="text-foreground">Contatta su WhatsApp</strong> sul mio profilo pubblico (solo Premium Silver/Gold).
              </span>
            </label>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Cucine</Label>
          <CuisineComboboxMulti
            value={form.cuisines}
            onChange={(arr) => setForm({ ...form, cuisines: arr })}
            placeholder="Seleziona le cucine che proponi"
            ariaLabel="Seleziona cucine"
          />
          <p className="text-xs text-muted-foreground">Cerca per nome, gruppo o parola chiave (es. "veg", "sushi").</p>
        </div>

        <div className="space-y-1.5">
          <Label>Specialità (max {MAX_SPECIALTIES})</Label>
          <CuisineComboboxMulti
            value={form.specialties}
            onChange={(arr) => setForm({ ...form, specialties: arr })}
            placeholder="Le tue 5 specialità principali"
            ariaLabel="Seleziona specialità"
            max={MAX_SPECIALTIES}
          />
          <p className="text-xs text-muted-foreground">Le specialità appaiono come badge nella tua card e nei risultati di ricerca.</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="languages">Lingue parlate (separate da virgola)</Label>
          <Input id="languages" value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} />
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {form.is_visible ? <Eye className="h-4 w-4 text-gold" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
                <Label className="text-sm font-medium">Visibilità profilo</Label>
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {form.is_visible
                  ? "Il tuo profilo è visibile pubblicamente: appare nella ricerca e sulla mappa."
                  : "Profilo temporaneamente nascosto: non appare nella ricerca né sulla mappa. Foto, recensioni e Premium restano salvati."}
              </p>
            </div>
            <Switch
              checked={form.is_visible}
              onCheckedChange={(c) => setForm({ ...form, is_visible: c })}
              aria-label="Mostra il profilo pubblicamente"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button type="submit" disabled={saving} className="bg-foreground text-background hover:bg-foreground/90">
            <Save className="mr-1.5 h-4 w-4" />
            {saving ? "Salvataggio..." : isNew ? "Crea profilo e attiva bonus" : "Salva modifiche"}
          </Button>
          <Button asChild variant="outline" type="button">
            <Link to="/dashboard">Annulla</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}